import { Component } from '@angular/core';
import { URLS } from 'src/app/data/navigation/navigation.data';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';
import { PRODUCT_HEROES } from '../product-hero.data';
import { ProductShots, Shot } from '../product-shots';
import { DOCK_TOWN_FEATURES, DockTownFeature, DockTownScene, dockTownSceneSrc, dockTownShot, dockTownShots } from 'src/app/data/product/dock-town-features.data';

// Promo landing page for Dock Town, the menu bar app that shows how the Mac is doing as a small
// isometric town, served at /plugins/macos/dock-town (its privacy policy at
// /plugins/macos/dock-town/privacy). Meant to be the app's marketing URL in App Store Connect,
// so it has to stay live and stay accurate: the copy describes only what the app does, checked
// against its sources (2026-10-08). SEO is applied by the NavigationInterceptor from
// navigation.data.ts (client) and seo-route-meta.js (server). Laid out as every Apps & Plugins
// page (../product-page.scss): the town under the menu bar on a band of its own color, then one
// section per thing the app does (DOCK_TOWN_FEATURES, shared with the home's showcase: the App
// Store pictures' headlines as text, their scenes as pictures of their own; owner 2026-10-10),
// the features without a picture as a list, then the questions; the pictures' lightbox is the
// shared one (../product-shots.ts).
@Component( {
  selector: 'app-macos-dock-town-page',
  templateUrl: './macos-dock-town-page.component.html',
  styleUrls: [
    '../chrome-color-picker-page/chrome-color-picker-page.component.scss',
    '../macos-tidy-desktop-page/macos-tidy-desktop-page.component.scss',
    '../product-page.scss',
    './macos-dock-town-page.component.scss',
  ],
} )
export class MacosDockTownPageComponent extends ProductShots {

  readonly URLS = URLS;

  // Flip to true once the Mac App Store listing is public, with appStoreUrl set to it: until
  // then the CTAs say it's coming soon instead of linking to a listing that doesn't exist yet.
  readonly released = false;
  readonly appStoreUrl = '';

  // The app has no icon yet (2026-10-07): the heart of its Tokyo town stands in beside its
  // name, and on the apps lists (product.data.ts). Replace the file once the icon is ready.
  readonly icon = './assets/product-lines/tools/dock-town/icon.png';

  // The product on its stage at the top of the page (product-hero.data.ts), and the facts as
  // chips under the pitch.
  readonly hero = PRODUCT_HEROES[ 'macos-dock-town' ];
  readonly facts = [ 'Free With In-App Purchases', 'macOS 26 Or Later' ];   // the towns have their own section (owner 2026-10-10: no 20+ Towns chip)

  // What the app does, a section each with its headline and pictures; the first, the town in the
  // menu bar, is the stage picture, so its section is the text under the stage.
  readonly intro: DockTownFeature = DOCK_TOWN_FEATURES[ 0 ];
  readonly pictured: DockTownFeature[] = DOCK_TOWN_FEATURES.slice( 1 );

  // What else it does, without a picture: three boxes for now (owner 2026-10-10).
  readonly features = [
    {
      title: 'A Town You Can Click',
      description: 'The road opens Activity Monitor, a house its Memory tab, the battery sign Battery settings and the sky Wi-Fi settings. Click an empty plot to build a house there.',
    },
    {
      title: 'Your Next Meeting',
      description: 'With calendar access, which is optional, your next meeting shows beside the town with a Join button for its video link.',
    },
    {
      title: 'Private by Design',
      description: 'No account, no tracking and no ads. The app makes no network requests, and everything stays on your Mac.',
    },
  ];

  // Every scene of every feature, the menu bar town first (it is the hero, product-hero.data.ts, so
  // it opens from the stage): each opens in the lightbox at full size (the -large file, loaded only
  // then), where the arrows page through them all and the picture's name shows under it.
  readonly shots: Shot[] = dockTownShots();

  // The questions the page shows, word for word the FAQPage JSON-LD the server sends
  // (DOCK_TOWN_FAQ in seo-route-meta.js): keep both in step.
  readonly faq = [
    { q: 'What is Dock Town?', a: 'Dock Town is a menu bar app for Mac that shows how your Mac is doing as a small isometric town. The traffic is the CPU, lit windows are memory, the sky is the network, a sign in the corner is the battery and smoke over the roofs is heat, and the town grows while you work.' },
    { q: 'How do I find out what is slowing down my Mac?', a: 'Click the landmark in the middle of your town. Its status card shows the battery, CPU, memory, network and heat in plain words, and when something is wrong it names the cause, like an app keeping a whole core busy in the background, memory nearly full or the battery draining fast, with buttons to switch to that app or quit it. Activity lists the apps at work with their CPU, memory and running time.' },
    { q: 'Does Dock Town show CPU and memory usage in the menu bar?', a: 'Yes. Its little icon in the menu bar moves faster the busier the processor is, and one click opens the town, where every vehicle is one of the apps at work: hover over one to see its name and share of the CPU. The status card gives the battery, CPU, memory, network and heat at a glance.' },
    { q: 'How does the town grow?', a: 'Time spent working at your Mac earns coins, and coins build houses, one plot at a time. When every plot is built, the town can go up a level and gets room for more houses. A break after a long stretch of work earns a bonus.' },
    { q: 'Is Dock Town free?', a: 'Yes. Dock Town is free with optional in-app purchases. Of its 20+ towns, 12 are free; the others can be bought one by one, or all opened with Town Pass, a monthly or yearly subscription. Coin packs build faster, but coins never open a town.' },
    { q: 'What happens to my towns when Town Pass ends?', a: 'The towns it opened lock again, but their houses and levels are kept, so they are as you left them when you open them again. A town bought on its own stays yours.' },
    { q: 'Does Dock Town collect my data?', a: 'No. There is no account, no analytics, no ads and no tracking, and the app makes no network requests of its own. Everything it reads to draw the town stays on your Mac.' },
    { q: 'Is Dock Town a productivity game?', a: 'In a gentle way. The town grows from the time you spend working, with no timer to start: work apps count in full, browsers, mail and chat half, music, videos and games not at all. A short break after a long stretch of work earns a bonus, and after eight hours the town rests until the next day.' },
    { q: 'Which Macs does Dock Town run on?', a: 'Macs with macOS 26 or later. On Macs with Apple Intelligence, it also describes the apps its built-in guide doesn\'t know, on-device.' },
  ];

  constructor( private analyticsService: AnalyticsService ) {
    super();
  }

  src( scene: DockTownScene ): string {
    return dockTownSceneSrc( scene );
  }

  /** The lightbox entry of a scene (the same object as in `shots`, so the arrows know where it is). */
  enlarge( scene: DockTownScene ): void {
    this.openLightbox( this.shots.find( s => s.src === this.src( scene ) ) || dockTownShot( scene ) );
  }

  trackFeature( _: number, feature: DockTownFeature ): string {
    return feature.key;
  }

  trackScene( _: number, scene: DockTownScene ): string {
    return scene.key;
  }

  onInstall(): void {
    this.analyticsService.logEvent( 'App Install Clicked', { app: 'macos-dock-town' } );
  }
}
